import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Smartphone,
  Mail,
  MessageSquare,
  Monitor,
  Trash2,
  RefreshCw,
  Loader,
  Copy,
  Check,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { mfaApi } from '../../services/authApi';
import type { MfaStatusResponse, TrustedDeviceInfo } from '../../types';

export const MfaSettingsSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<MfaStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesCopied, setCodesCopied] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await mfaApi.getStatus();
      setStatus(data);
    } catch {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleDisable = async () => {
    setError('');
    setActionLoading(true);
    try {
      await mfaApi.disable(disablePassword);
      setShowDisableConfirm(false);
      setDisablePassword('');
      await fetchStatus();
    } catch (err: any) {
      setError(err?.message || 'Failed to disable MFA. Check your password.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setError('');
    setActionLoading(true);
    try {
      const result = await mfaApi.regenerateBackupCodes();
      setBackupCodes(result.backupCodes);
      setShowBackupCodes(true);
      await fetchStatus();
    } catch (err: any) {
      setError(err?.message || 'Failed to regenerate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeDevice = async (deviceId: string) => {
    setActionLoading(true);
    try {
      await mfaApi.revokeDevice(deviceId);
      await fetchStatus();
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke device');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAllDevices = async () => {
    setActionLoading(true);
    try {
      await mfaApi.revokeAllDevices();
      await fetchStatus();
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke devices');
    } finally {
      setActionLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCodesCopied(true);
    setTimeout(() => setCodesCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `ClinLexis MFA Backup Codes\n${'='.repeat(40)}\n\n${backupCodes.join('\n')}\n\nKeep these codes somewhere safe.\nEach code can only be used once.\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clinlexis-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="card dark:bg-medical-800 dark:border-medical-700">
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="card dark:bg-medical-800 dark:border-medical-700">
      <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-4 flex items-center space-x-2">
        <Shield className="h-5 w-5" />
        <span>Two-Factor Authentication</span>
      </h3>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!status?.mfaEnabled ? (
        /* MFA Not Enabled */
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <ShieldX className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">MFA is not enabled</p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/settings/mfa')}
            className="btn-primary flex items-center space-x-2"
          >
            <Shield className="h-4 w-4" />
            <span>Set Up MFA</span>
          </button>
        </div>
      ) : (
        /* MFA Enabled */
        <div className="space-y-5">
          <div className="flex items-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <ShieldCheck className="h-6 w-6 text-green-600 dark:text-green-400 shrink-0" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-300">MFA is enabled</p>
              <p className="text-sm text-green-700 dark:text-green-400">
                Your account is protected with two-factor authentication.
              </p>
            </div>
          </div>

          {/* Configured methods */}
          <div>
            <h4 className="text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">Configured Methods</h4>
            <div className="space-y-2">
              {status.totpConfigured && (
                <div className="flex items-center space-x-2 text-sm text-medical-700 dark:text-medical-300">
                  <Smartphone className="h-4 w-4 text-primary-500" />
                  <span>Authenticator App</span>
                  {status.preferredMethod === 'TOTP' && (
                    <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">Preferred</span>
                  )}
                </div>
              )}
              {status.emailOtpEnabled && (
                <div className="flex items-center space-x-2 text-sm text-medical-700 dark:text-medical-300">
                  <Mail className="h-4 w-4 text-primary-500" />
                  <span>Email OTP</span>
                  {status.preferredMethod === 'EMAIL' && (
                    <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">Preferred</span>
                  )}
                </div>
              )}
              {status.smsConfigured && (
                <div className="flex items-center space-x-2 text-sm text-medical-700 dark:text-medical-300">
                  <MessageSquare className="h-4 w-4 text-primary-500" />
                  <span>SMS ({status.smsPhone})</span>
                  {status.preferredMethod === 'SMS' && (
                    <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">Preferred</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Backup codes */}
          <div>
            <h4 className="text-sm font-medium text-medical-700 dark:text-medical-300 mb-2">Backup Codes</h4>
            <p className="text-sm text-medical-500 dark:text-medical-400 mb-2">
              {status.backupCodesRemaining} backup codes remaining
            </p>
            {status.backupCodesRemaining <= 2 && (
              <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400 text-sm mb-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Low backup codes. Consider regenerating.</span>
              </div>
            )}
            <button
              onClick={handleRegenerateBackupCodes}
              disabled={actionLoading}
              className="btn-secondary flex items-center space-x-2 text-sm"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span>Regenerate Backup Codes</span>
            </button>
          </div>

          {/* Backup codes modal */}
          {showBackupCodes && backupCodes.length > 0 && (
            <div className="bg-medical-50 dark:bg-medical-700 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-medical-800 dark:text-medical-200">New Backup Codes</p>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <code key={i} className="text-sm font-mono text-center py-1.5 bg-white dark:bg-medical-800 rounded border border-medical-200 dark:border-medical-600">
                    {code}
                  </code>
                ))}
              </div>
              <div className="flex space-x-2">
                <button onClick={copyBackupCodes} className="btn-secondary flex items-center space-x-1 text-xs flex-1">
                  {codesCopied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  <span>{codesCopied ? 'Copied!' : 'Copy'}</span>
                </button>
                <button onClick={downloadBackupCodes} className="btn-secondary flex items-center space-x-1 text-xs flex-1">
                  <Download className="h-3 w-3" />
                  <span>Download</span>
                </button>
              </div>
              <button onClick={() => setShowBackupCodes(false)} className="text-sm text-primary-600 hover:text-primary-700">
                Close
              </button>
            </div>
          )}

          {/* Trusted devices */}
          {status.trustedDevices.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-medical-700 dark:text-medical-300">Trusted Devices</h4>
                <button
                  onClick={handleRevokeAllDevices}
                  disabled={actionLoading}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Revoke all
                </button>
              </div>
              <div className="space-y-2">
                {status.trustedDevices.map((device: TrustedDeviceInfo) => (
                  <div key={device.id} className="flex items-center justify-between p-3 bg-medical-50 dark:bg-medical-700 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Monitor className="h-4 w-4 text-medical-400" />
                      <div>
                        <p className="text-sm text-medical-800 dark:text-medical-200">{device.deviceName}</p>
                        <p className="text-xs text-medical-500 dark:text-medical-400">
                          Expires {new Date(device.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeDevice(device.id)}
                      disabled={actionLoading}
                      className="text-red-500 hover:text-red-600 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disable MFA */}
          <div className="pt-2 border-t border-medical-200 dark:border-medical-600">
            {!showDisableConfirm ? (
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Disable MFA
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-medical-600 dark:text-medical-400">
                  Enter your password to disable MFA. This will remove all configured methods and trusted devices.
                </p>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="input-field"
                  placeholder="Current password"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <button onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); }} className="btn-secondary text-sm">
                    Cancel
                  </button>
                  <button
                    onClick={handleDisable}
                    disabled={actionLoading || !disablePassword}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 flex items-center space-x-2"
                  >
                    {actionLoading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : null}
                    <span>Confirm Disable</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
